import React, { memo, useMemo } from 'react';

import { Box } from '@mui/material';
import SyntaxHighlighterComponent from 'react-syntax-highlighter';
import { nightOwl } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import type { SxProps, Theme } from '@mui/material/styles';

const MAX_HEIGHT = 200;

const csvBoxSx: SxProps<Theme> = theme => ({
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto',
  lineHeight: theme.typography.pxToRem(24),
  overflow: 'auto',
  padding: '14px',
  backgroundColor: 'gx.response',
  maxHeight: MAX_HEIGHT,
  fontSize: theme.typography.pxToRem(14)
});

const codeBoxSx: SxProps<Theme> = theme => ({
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto',
  fontSize: theme.typography.pxToRem(14)
});

const baseCustomStyle: React.CSSProperties = {
  margin: 0,
  padding: '14px',
  backgroundColor: 'gx.response',
  lineHeight: '24px',
  overflow: 'auto',
  minWidth: 0,
  width: '100%',
  overflowX: 'auto',
  fontSize: 'inherit',
  maxHeight: MAX_HEIGHT
};

export const SyntaxHighlighter = memo(function SyntaxHighlighter({
  language,
  fileContent
}: {
  fileContent: string;
  language: string;
}) {
  // Check if content contains URLs (for http, api, json types with URLs)
  const hasLongLines = language === 'text' || language === 'http';

  const customStyle = useMemo<React.CSSProperties>(
    () => ({
      ...baseCustomStyle,
      wordBreak: hasLongLines ? 'break-all' : 'normal',
      overflowWrap: hasLongLines ? 'anywhere' : 'normal',
      whiteSpace: hasLongLines ? 'pre-wrap' : 'pre'
    }),
    [hasLongLines]
  );

  if (language === 'csv') {
    return <Box sx={csvBoxSx}>{fileContent}</Box>;
  }

  return (
    <Box sx={codeBoxSx}>
      <SyntaxHighlighterComponent
        customStyle={customStyle}
        language={language}
        style={nightOwl}
        wrapLongLines={hasLongLines}
      >
        {fileContent}
      </SyntaxHighlighterComponent>
    </Box>
  );
});
